import React, { useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, Loader2, ListChecks, Printer } from 'lucide-react';
import { adminVendorDocumentsApi } from '../../services/adminVendorDocumentsService';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';
import InvoiceLivePreview from '../../components/vendor/invoice/InvoiceLivePreview';
import { downloadInvoicePdfFromElement } from '../../utils/vendorInvoicePdf';
import { useToastContext } from '../../contexts/ToastContext';
import Button from '../../components/shared/Button';

const AdminVendorInvoiceDetail = () => {
 const { id, invoiceId } = useParams();
 const { error: showError } = useToastContext();
 const printRef = useRef(null);
 const [pdfLoading, setPdfLoading] = useState(false);

 const { data, isLoading, error } = useQuery({
 queryKey: ['admin-vendor-invoice', id, invoiceId],
 queryFn: () => adminVendorDocumentsApi.getInvoice(id, invoiceId),
 enabled: !!id && !!invoiceId,
 });

 const invoice = data?.data;
 const vendorMeta = data?.vendor;
 const vp = invoice?.vendor || {};

 const vendorLabel = vendorMeta?.business_name || vp.business_name || 'Vendor';
 const vendorLogoUrl = invoice?.logo_url || vp.logo_url || vp.brand_settings?.logo_url || vendorMeta?.logo_url || null;
 const vendorAddressLines = [
 vp.address,
 [vp.city, vp.state, vp.zip_code].filter(Boolean).join(', '),
 vp.country,
 vp.business_phone,
 ].filter(Boolean);

 const previewLines = useMemo(() => {
 if (!invoice) return [];
 if (invoice.line_items?.length) {
 return invoice.line_items.map((row) => ({
 description: row.description,
 quantity: Number(row.quantity),
 unit_price: Number(row.unit_price),
 is_addon: Boolean(row.is_addon),
 addon_name: row.addon_name || '',
 }));
 }
 return [
 {
 description: (invoice.document_type || 'invoice') === 'receipt' ? 'Receipt items' : 'Invoice items',
 quantity: 1,
 unit_price: Number(invoice.subtotal ?? invoice.total_amount ?? 0),
 },
 ];
 }, [invoice]);

 const formatDisplayDate = (val) => {
 if (!val) return '';
 const s = typeof val === 'string' ? val.slice(0, 10) : val;
 try {
 return new Date(`${s}T12:00:00`).toLocaleDateString(undefined, {
 year: 'numeric',
 month: 'short',
 day: 'numeric',
 });
 } catch {
 return String(val);
 }
 };

 const handleDownload = async () => {
 if (!invoice || !printRef.current) return;
 setPdfLoading(true);
 try {
 const isReceipt = (invoice.document_type || 'invoice') === 'receipt';
 const name = `${isReceipt ? 'receipt' : 'invoice'}-${invoice.invoice_number || invoice.id}.pdf`;
 await downloadInvoicePdfFromElement(printRef.current, name);
 } catch {
 showError('Download failed', 'Could not generate PDF.');
 } finally {
 setPdfLoading(false);
 }
 };

 if (isLoading) {
 return (
 <div className="space-y-5">
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-gray-500">Loading invoice…</p>
 </div>
 </div>
 );
 }

 if (error || !invoice) {
 return (
 <div className="space-y-5">
 <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
 <p className="font-semibold text-red-800 mb-4">Invoice not found</p>
 <Link
 to={`/admin/vendors/${id}/invoices`}
 className="inline-flex items-center gap-1.5 text-sm text-ink hover:underline"
 >
 <ArrowLeft className="w-4 h-4" />
 Back to invoices
 </Link>
 </div>
 </div>
 );
 }

 const isReceipt = (invoice.document_type || 'invoice') === 'receipt';

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title={invoice.invoice_number || 'Invoice'}
 subtitle={`${vendorLabel} · ${isReceipt ? 'Receipt' : 'Invoice'} for ${
 invoice.client_name || invoice.client?.name || invoice.guest_name || 'client'
 }`}
 actions={
 <div className="flex flex-wrap items-center gap-2">
 <Link
 to={`/admin/vendors/${id}/invoices`}
 className="inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 px-3.5 py-1.5 text-xs font-medium rounded-md border border-border bg-surface text-ink hover:bg-surface-muted"
 >
 <ArrowLeft className="w-4 h-4 shrink-0" />
 Back to invoices
 </Link>
 <Button
 variant="outline"
 className="rounded-md whitespace-nowrap"
 onClick={() => window.print()}
 >
 <Printer className="w-4 h-4 mr-1.5" />
 Print
 </Button>
 <Button
 className="rounded-md whitespace-nowrap"
 onClick={handleDownload}
 disabled={pdfLoading}
 >
 {pdfLoading ? (
 <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
 ) : (
 <Download className="w-4 h-4 mr-1.5" />
 )}
 PDF
 </Button>
 </div>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.vendorInvoiceDetail.title}
 steps={ADMIN_PAGE_GUIDES.vendorInvoiceDetail.steps}
 icon={ListChecks}
 />

 <div className="product-panel p-4 sm:p-6 overflow-hidden">
 <div ref={printRef}>
 <InvoiceLivePreview
 vendorLabel={vendorLabel}
 vendorLogoUrl={vendorLogoUrl}
 vendorAddressLines={vendorAddressLines}
 billToName={invoice.client_name || invoice.client?.name || invoice.guest_name}
 billToEmail={invoice.client?.email || invoice.guest_email}
 billToPhone={invoice.client?.phone || invoice.guest_phone}
 invoiceNumberPreview={invoice.invoice_number}
 invoiceDate={formatDisplayDate(invoice.invoice_date)}
 dueDate={formatDisplayDate(invoice.due_date)}
 lineItems={previewLines}
 taxRate={invoice.tax_rate}
 subtotal={invoice.subtotal}
 discountAmount={invoice.discount_amount}
 discountType={invoice.discount_type}
 discountValue={invoice.discount_value}
 taxAmount={invoice.tax_amount}
 total={invoice.total_amount}
 notes={invoice.notes}
 paymentTerms={invoice.payment_terms}
 bookingRef={invoice.booking?.booking_reference}
 statusLabel={invoice.status}
 documentType={invoice.document_type || 'invoice'}
 />
 </div>
 </div>
 </div>
 );
};

export default AdminVendorInvoiceDetail;
